/*
  CODEX CUSTOMIZATION START
  File: custom-wishlist-hydration.js
  Purpose:
  - Hydrate Wishlist Power's guest/customer wishlist outside the wishlist page.
  - Update existing Wishlist Power count and heart controls without per-card requests.
  CODEX CUSTOMIZATION END
*/

(() => {
  const WISHLIST_READY_EVENT = 'ooo-wishlist:ready';
  const COUNT_SELECTOR = 'ooo-wl-wishlist-count';
  const CARD_BUTTON_SELECTOR = 'ooo-wl-product-card-button';
  const CLICKABLE_BUTTON_SELECTOR = 'ooo-wl-product-card-button, ooo-wl-wishlist-button';
  const API_WAIT_TIMEOUT = 4000;
  const HYDRATION_KEY = '__codexWishlistHydrationStarted';
  const HYDRATION_RETRY_DELAYS = [0, 250, 1000, 2500];
  const hydrationState = {
    hydrationGeneration: 0,
    hydrationTimers: [],
  };
  let lastInteractedWishlistButton = null;
  let wishlistAnnouncementTimer = null;
  const wishlistAnnouncements = [];
  const lastWishlistOutcomes = new Map();
  const wishlistCardObservers = new WeakMap();
  const wishlistTransactions = new Map();
  let wishlistLabelId = 0;

  function getWishlistApi() {
    const wishlistApi = window.Maestrooo?.wishlist;

    return wishlistApi && typeof wishlistApi.getContent === 'function' ? wishlistApi : null;
  }

  function waitForWishlistApi() {
    const api = getWishlistApi();

    if (api) {
      return Promise.resolve(api);
    }

    return new Promise((resolve) => {
      let timeoutId;

      const complete = () => {
        window.clearTimeout(timeoutId);
        document.removeEventListener(WISHLIST_READY_EVENT, complete);
        resolve(getWishlistApi());
      };

      document.addEventListener(WISHLIST_READY_EVENT, complete, { once: true });
      timeoutId = window.setTimeout(complete, API_WAIT_TIMEOUT);
    });
  }

  function refreshWishlistCount() {
    document.querySelectorAll(COUNT_SELECTOR).forEach((countElement) => {
      // Wishlist Power refreshes its own count when an existing count attribute is removed.
      // The API result is now cached, so this is a local read rather than another request.
      if (!countElement.hasAttribute('count')) {
        countElement.setAttribute('count', '0');
      }

      countElement.removeAttribute('count');
    });
  }

  function getWishlistButtonControl(buttonElement) {
    return buttonElement.wishlistButton
      || buttonElement.button
      || buttonElement.shadowRoot?.querySelector('button')
      || buttonElement.querySelector('button');
  }

  function getWishlistButtonHandle(buttonElement) {
    if (typeof buttonElement.handle === 'string' && buttonElement.handle !== '') {
      return buttonElement.handle;
    }

    return buttonElement.getAttribute('handle');
  }

  function nameWishlistControl(control) {
    const name = window.themeVariables?.strings?.wishlistControlLabel || 'Wishlist';
    let label = control.querySelector('[data-codex-wishlist-label]');
    if (!label) {
      label = document.createElement('span');
      label.id = `codex-wishlist-control-label-${++wishlistLabelId}`;
      label.hidden = true;
      label.setAttribute('data-codex-wishlist-label', '');
      control.appendChild(label);
    }
    if (label.textContent !== name) {
      label.textContent = name;
    }
    // A same-root referenced label takes precedence over the app's transient
    // Add/Remove aria-label writes. The checkbox name remains stable.
    if (control.getAttribute('aria-labelledby') !== label.id) {
      control.setAttribute('aria-labelledby', label.id);
    }
    // Leave the app's fallback aria-label alone; rewriting it on each native
    // event would add unnecessary mutations without changing this name.
  }

  // Keep product-card focus order logical without changing the visual layout.
  // The media remains the first visual grid row, while the product information
  // is moved before it in the DOM so the title is encountered before the
  // Quick Add and Wishlist controls inside the media figure.
  function normalizeProductCardDomOrder(card) {
    const figure = card.querySelector(':scope > .product-card__figure');
    const info = card.querySelector(':scope > .product-card__info');

    if (!figure || !info || info.nextElementSibling === figure) {
      return;
    }

    card.insertBefore(info, figure);
  }

  function normalizeProductCardDomOrders() {
    document.querySelectorAll('product-card').forEach(normalizeProductCardDomOrder);
  }

  function clearWishlistHydrationTimers(state) {
    state.hydrationTimers.forEach((timerId) => {
      window.clearTimeout(timerId);
    });

    state.hydrationTimers = [];
  }

  function cancelWishlistHydrationRetries() {
    hydrationState.hydrationGeneration += 1;
    clearWishlistHydrationTimers(hydrationState);
  }

  function getWishlistAnnouncementRegion() {
    let region = document.getElementById('codex-wishlist-announcement');

    if (region instanceof HTMLElement) {
      return region;
    }

    region = document.createElement('div');
    region.id = 'codex-wishlist-announcement';
    region.className = 'sr-only';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    region.setAttribute('aria-relevant', 'additions text');
    document.body?.appendChild(region);

    return region;
  }

  function publishNextWishlistAnnouncement() {
    if (wishlistAnnouncementTimer !== null || wishlistAnnouncements.length === 0) {
      return;
    }

    const region = getWishlistAnnouncementRegion();
    const message = wishlistAnnouncements.shift();
    region.textContent = '';
    wishlistAnnouncementTimer = window.setTimeout(() => {
      region.textContent = message;
      wishlistAnnouncementTimer = window.setTimeout(() => {
        wishlistAnnouncementTimer = null;
        publishNextWishlistAnnouncement();
      }, 500);
    }, 100);
  }

  function announceWishlistChange(isSaved) {
    const translations = window.ooo?.wishlist?.translations || {};
    const message = isSaved
      ? (translations.added_to_wishlist || window.themeVariables?.strings?.wishlistAdded || 'The product was added to your wishlist.')
      : (translations.removed_from_wishlist || window.themeVariables?.strings?.wishlistRemoved || 'The product was removed from your wishlist.');
    wishlistAnnouncements.push(message);
    publishNextWishlistAnnouncement();
  }

  function normalizeWishlistToggleButton(buttonElement, control, isSaved, translations) {
    const transaction = wishlistTransactions.get(getWishlistButtonHandle(buttonElement));
    if (transaction && !transaction.settled && transaction.controls.has(control)) {
      isSaved = transaction.controls.get(control).saved;
    }
    const checked = String(isSaved);

    const shadowRoot = buttonElement.shadowRoot;

    if (shadowRoot && !shadowRoot.querySelector('#codex-wishlist-card-state-style')) {
      const style = document.createElement('style');
      style.id = 'codex-wishlist-card-state-style';
      style.textContent = `
        :host([data-codex-wishlist-saved="true"]) .ooo-wl-button-icon path,
        button[data-codex-wishlist-saved="true"] .ooo-wl-button-icon path {
          fill: #3f534b !important;
          stroke: #3f534b !important;
        }
      `;
      shadowRoot.appendChild(style);
    }

    // Expose only the actual, focusable control. Giving its custom-element
    // wrapper the same interactive role/name creates a second AX control.
    ['role', 'aria-label', 'aria-pressed', 'aria-checked'].forEach((attribute) => {
      if (buttonElement.hasAttribute(attribute)) {
        buttonElement.removeAttribute(attribute);
      }
    });

    // Wishlist Power reads aria-checked to choose add versus remove. Restore
    // its checkbox pattern instead of combining checked with role=button.
    // The native button retains its click, Enter and Space handling.
    if (control.hasAttribute('aria-pressed')) {
      control.removeAttribute('aria-pressed');
    }
    if (control.getAttribute('role') !== 'checkbox') {
      control.setAttribute('role', 'checkbox');
    }
    if (control.getAttribute('aria-checked') !== checked) {
      control.setAttribute('aria-checked', checked);
    }
    nameWishlistControl(control);
    control.querySelectorAll('svg.ooo-wl-button-icon').forEach((icon) => {
      if (icon.getAttribute('aria-hidden') !== 'true') {
        icon.setAttribute('aria-hidden', 'true');
      }
    });

    [buttonElement, control].forEach((element) => {
      if (!(element instanceof HTMLElement)) {
        return;
      }

      if (element.getAttribute('data-codex-wishlist-saved') !== checked) {
        element.setAttribute('data-codex-wishlist-saved', checked);
      }
    });
  }

  function observeWishlistCardControl(buttonElement, control) {
    const existing = wishlistCardObservers.get(buttonElement);

    if (existing?.control === control) {
      return;
    }

    existing?.observer.disconnect();

    const observer = new MutationObserver(() => {
      const checked = control.getAttribute('aria-checked');
      const pressed = control.getAttribute('aria-pressed');

      if (checked !== 'true' && checked !== 'false' && pressed !== 'true' && pressed !== 'false') {
        return;
      }

      const isSaved = checked === 'true' || (checked !== 'false' && pressed === 'true');
      normalizeWishlistToggleButton(
        buttonElement,
        control,
        isSaved,
        window.ooo?.wishlist?.translations || {}
      );
    });

    observer.observe(control, {
      attributes: true,
      attributeFilter: ['aria-checked', 'aria-pressed', 'aria-label', 'data-codex-wishlist-saved'],
    });
    wishlistCardObservers.set(buttonElement, { control, observer });
  }

  function normalizeCurrentWishlistControls() {
    document.querySelectorAll(CLICKABLE_BUTTON_SELECTOR).forEach((host) => {
      const control = getWishlistButtonControl(host);
      const checked = control?.getAttribute('aria-checked');
      if (checked !== 'true' && checked !== 'false') return;
      normalizeWishlistToggleButton(host, control, checked === 'true');
      observeWishlistCardControl(host, control);
    });
  }

  function getWishlistEventHandle(event) {
    const detail = event?.detail || {};
    const candidates = [
      detail.handle,
      detail.productHandle,
      detail.product_handle,
      detail.product?.handle,
      detail.product?.productHandle,
      detail.product?.product_handle,
      detail.item?.handle,
      detail.item?.product?.handle,
    ];

    return candidates.find((candidate) => typeof candidate === 'string' && candidate !== '') || '';
  }

  function getWishlistVariant(buttonElement, functions) {
    if (!functions.isVariantUseActive?.()) {
      return null;
    }
    if (buttonElement.matches(CARD_BUTTON_SELECTOR)) {
      const href = buttonElement.imageLink?.getAttribute('href');
      return href ? new URL(href, window.location.origin).searchParams.get('variant') : null;
    }
    return buttonElement.currentVariantId || null;
  }

  async function updateWishlistWithoutDroppingFocus(buttonElement, control, functions, api) {
    const handle = getWishlistButtonHandle(buttonElement);
    const wasSaved = control.getAttribute('aria-checked') === 'true';
    const transaction = { controls: new Map(), settled: false, products: null };
    wishlistTransactions.set(handle, transaction);
    cancelWishlistHydrationRetries();

    document.querySelectorAll(CLICKABLE_BUTTON_SELECTOR).forEach((host) => {
      if (getWishlistButtonHandle(host) !== handle) return;
      const current = getWishlistButtonControl(host);
      if (!current) return;
      transaction.controls.set(current, {
        host, saved: current.getAttribute('aria-checked') === 'true',
        variant: getWishlistVariant(host, functions),
      });
      // Keep the native button focusable. A per-product lock below prevents
      // duplicate requests; disabled/aria-disabled would announce "dimmed".
      current.setAttribute('aria-busy', 'true');
      normalizeWishlistToggleButton(host, current, current.getAttribute('aria-checked') === 'true');
      observeWishlistCardControl(host, current);
    });

    let verified = false;
    let operationError = false;
    try {
      // Preserve Wishlist Power's product/variant selection, remove-all logic,
      // success events, counts and integrations. Only bypass the entry point
      // that disables/blurs the real control and optimistically flips it.
      await buttonElement.toggleButtonManager(wasSaved);
    } catch (error) {
      operationError = true;
    }
    try {
      // The vendor manager catches some request errors and can emit one
      // removal event per variant. Reconcile after ALL work, not first event.
      // Successful native events include the server's full product snapshot.
      // Prefer the final event over a redundant, potentially failing request.
      const content = transaction.products ? { products: transaction.products } : await api.getContent(false);
      if (!Array.isArray(content?.products)) throw new Error('Wishlist content unavailable');
      for (const record of transaction.controls.values()) {
        record.finalSaved = await functions.containsProduct(handle, record.variant, record.host.productId);
        if (typeof record.finalSaved !== 'boolean') throw new Error('Wishlist state unavailable');
      }
      const finalSaved = transaction.controls.get(control)?.finalSaved;
      // Card removal / non-variant PDP removal must remove the entire product,
      // including variants that may have been saved on earlier visits.
      const removesWholeProduct = wasSaved && (buttonElement.matches(CARD_BUTTON_SELECTOR) || !functions.isVariantUseActive?.());
      const productRemains = content.products.some((product) => product.handle === handle);
      verified = finalSaved === !wasSaved && (!removesWholeProduct || !productRemains);
    } catch (error) {
      operationError = true;
    } finally {
      transaction.settled = true;
      transaction.controls.forEach((record, current) => {
        // A verified cached read can still update the UI after partial failure.
        // With no final read, keep the last confirmed state, not a prediction.
        const saved = typeof record.finalSaved === 'boolean' ? record.finalSaved : record.saved;
        normalizeWishlistToggleButton(record.host, current, saved);
        current.setAttribute('aria-busy', 'false');
      });
      wishlistTransactions.delete(handle);
    }

    if (verified) {
      lastWishlistOutcomes.set(handle, !wasSaved);
      announceWishlistChange(!wasSaved);
    } else {
      // Do not announce a successful add/remove on a rejected/partial request.
      wishlistAnnouncements.push(window.themeVariables?.strings?.wishlistUpdateError
        || 'We could not update your wishlist. Please try again.');
      publishNextWishlistAnnouncement();
    }
    if (operationError) {
      console.debug('[Wishlist Power] Wishlist update required reconciliation.');
    }
  }

  function handleWishlistActivation(event) {
    const buttonElement = event.composedPath().find((node) => node instanceof Element && node.matches(CLICKABLE_BUTTON_SELECTOR));
    if (!buttonElement || event.defaultPrevented) return;
    const control = getWishlistButtonControl(buttonElement);
    const handle = getWishlistButtonHandle(buttonElement);
    lastInteractedWishlistButton = buttonElement;
    cancelWishlistHydrationRetries();
    const functions = window.ooo?.wishlist?.functions;
    const api = getWishlistApi();
    // Leave login/access handling and unsupported future app versions native.
    if (!handle || !control || !api || typeof buttonElement.toggleButtonManager !== 'function'
      || typeof functions?.containsProduct !== 'function' || !functions.isWishlistAccessible?.()) return;
    if (control.disabled || buttonElement.hasAttribute('loading')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    lastInteractedWishlistButton = null;
    if (wishlistTransactions.has(handle) || control.getAttribute('aria-busy') === 'true') return;
    if (control.getAttribute('aria-checked') !== 'true'
      && typeof window.ooo.wishlist.__itemsLimit === 'function' && window.ooo.wishlist.__itemsLimit()) {
      // Keep the app's limit message and restrictions without disabling focus.
      buttonElement.showLimitMessage?.();
      return;
    }
    updateWishlistWithoutDroppingFocus(buttonElement, control, functions, api);
  }

  function syncWishlistButtonsAfterInteraction(event, isSaved) {
    const eventHandle = getWishlistEventHandle(event);
    const translations = window.ooo?.wishlist?.translations || {};

    document.querySelectorAll(CARD_BUTTON_SELECTOR).forEach((buttonElement) => {
      const handle = getWishlistButtonHandle(buttonElement);
      const isInteractedButton = buttonElement === lastInteractedWishlistButton;

      if (eventHandle && handle !== eventHandle && !isInteractedButton) {
        return;
      }

      if (!eventHandle && !isInteractedButton) {
        return;
      }

      const control = getWishlistButtonControl(buttonElement);

      if (control) {
        normalizeWishlistToggleButton(buttonElement, control, isSaved, translations);
        observeWishlistCardControl(buttonElement, control);
        control.removeAttribute('loading');
        buttonElement.removeAttribute('loading');
      }
    });

    // Do not immediately rehydrate from the API here. Its cache can lag behind
    // the just-completed mutation and roll the correct native state back. The
    // native Wishlist Power event handler remains responsible for its own count.
  }

  function refreshWishlistButtons(products) {
    const savedHandles = new Set(
      products
        .map((product) => product?.handle)
        .filter((handle) => typeof handle === 'string' && handle !== '')
    );
    const translations = window.ooo?.wishlist?.translations || {};

    document.querySelectorAll(CARD_BUTTON_SELECTOR).forEach((buttonElement) => {
      const card = buttonElement.closest('product-card');

      if (card) {
        normalizeProductCardDomOrder(card);
      }

      const handle = getWishlistButtonHandle(buttonElement);
      const control = getWishlistButtonControl(buttonElement);

      if (!handle || !control) {
        return;
      }

      const isSaved = savedHandles.has(handle);

      normalizeWishlistToggleButton(buttonElement, control, isSaved, translations);
      observeWishlistCardControl(buttonElement, control);
      control.removeAttribute('loading');
      buttonElement.removeAttribute('loading');
    });
  }

  // Keep the live region in the document before the first mutation so screen
  // readers observe later text changes reliably.
  if (document.body) {
    getWishlistAnnouncementRegion();
  } else {
    document.addEventListener('DOMContentLoaded', getWishlistAnnouncementRegion, { once: true });
  }

  normalizeProductCardDomOrders();
  normalizeCurrentWishlistControls();
  // App upgrades and AJAX-injected cards need the same semantics without an
  // extra hydration request or waiting for the first user activation.
  ['ooo-wl-product-card-button', 'ooo-wl-wishlist-button'].forEach((name) => {
    window.customElements?.whenDefined(name).then(normalizeCurrentWishlistControls);
  });

  if (document.body && typeof MutationObserver === 'function') {
    let orderNormalizationQueued = false;
    const queueProductCardDomOrderNormalization = () => {
      if (orderNormalizationQueued) {
        return;
      }

      orderNormalizationQueued = true;
      window.requestAnimationFrame(() => {
        orderNormalizationQueued = false;
        normalizeProductCardDomOrders();
        normalizeCurrentWishlistControls();
      });
    };

    const productCardObserver = new MutationObserver((records) => {
      if (records.some((record) => Array.from(record.addedNodes).some((node) => {
        return node.nodeType === Node.ELEMENT_NODE
          && (node.matches(`product-card, .product-card__figure, .product-card__info, ${CLICKABLE_BUTTON_SELECTOR}`)
            || node.querySelector(`product-card, .product-card__figure, .product-card__info, ${CLICKABLE_BUTTON_SELECTOR}`));
      }))) {
        queueProductCardDomOrderNormalization();
      }
    });

    productCardObserver.observe(document.body, { childList: true, subtree: true });
  }

  async function hydrateWishlist(state, { forceFresh = false, generation = 0 } = {}) {
    if (generation !== state.hydrationGeneration) {
      return;
    }

    const wishlistApi = await waitForWishlistApi();

    if (generation !== state.hydrationGeneration) {
      return;
    }

    if (!wishlistApi) {
      return;
    }

    try {
      // On first load we can use the app cache. Any explicitly forced refresh
      // should ask the app for its non-cached response.
      const content = await wishlistApi.getContent(forceFresh ? false : true);
      const products = Array.isArray(content?.products) ? content.products : [];

      if (generation !== state.hydrationGeneration) {
        return;
      }

      refreshWishlistCount();
      refreshWishlistButtons(products);
    } catch (error) {
      // Wishlist controls remain usable if the optional background hydration fails.
      console.debug('[Wishlist Power] Background wishlist hydration was unavailable.', error);
    }
  }

  function queueHydration({ forceFresh = false } = {}) {
    if (window[HYDRATION_KEY] && !forceFresh) {
      return;
    }

    if (!window[HYDRATION_KEY]) {
      window[HYDRATION_KEY] = true;
    }

    hydrationState.hydrationGeneration += 1;
    const generation = hydrationState.hydrationGeneration;

    clearWishlistHydrationTimers(hydrationState);

    const runHydration = () => {
      hydrateWishlist(hydrationState, { forceFresh, generation });
    };

    const runWhenIdle = () => {
      if (generation !== hydrationState.hydrationGeneration) {
        return;
      }

      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(runHydration, { timeout: 1200 });
      } else {
        window.setTimeout(runHydration, 0);
      }
    };

    if (document.readyState === 'complete') {
      runWhenIdle();
    } else {
      window.addEventListener('load', runWhenIdle, { once: true });
    }

    if (!forceFresh) {
      HYDRATION_RETRY_DELAYS.forEach((delay) => {
        const timerId = window.setTimeout(() => {
          if (generation !== hydrationState.hydrationGeneration) {
            return;
          }

          runHydration();
        }, delay);

        hydrationState.hydrationTimers.push(timerId);
      });
    }
  }

  queueHydration();

  [['ooo-wishlist:product-added', true], ['ooo-wishlist:product-removed', false]].forEach(([eventName, isSaved]) => {
    document.addEventListener(eventName, (event) => {
      const transaction = wishlistTransactions.get(getWishlistEventHandle(event));
      if (transaction) {
        const products = event.detail?.wishlist_product_list;
        if (Array.isArray(products)) transaction.products = products;
        lastInteractedWishlistButton = null;
        return;
      }
      cancelWishlistHydrationRetries();
      syncWishlistButtonsAfterInteraction(event, isSaved);
      // These events are dispatched after Wishlist Power confirms success.
      // A multi-variant removal may emit more than one event for one product.
      const product = event.detail?.product;
      const key = getWishlistEventHandle(event) || product?.product_id
        || (lastInteractedWishlistButton && getWishlistButtonHandle(lastInteractedWishlistButton));
      if (!key || lastWishlistOutcomes.get(String(key)) !== isSaved) {
        if (key) {
          lastWishlistOutcomes.set(String(key), isSaved);
        }
        announceWishlistChange(isSaved);
      }

      lastInteractedWishlistButton = null;
    });
  });

  document.addEventListener('click', handleWishlistActivation, true);

  document.addEventListener('keydown', (event) => {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    const wishlistButton = event.target instanceof Element
      ? event.target.closest(CLICKABLE_BUTTON_SELECTOR)
      : null;

    if (!wishlistButton || wishlistButton.hasAttribute('disabled')) {
      return;
    }

    lastInteractedWishlistButton = wishlistButton;
    cancelWishlistHydrationRetries();
  }, true);
})();
